import { inRange } from 'lodash'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import axios, { CanceledError } from 'axios'

import type { PaginationParams } from '@/shared/models/requestModels'

export interface RequestResponse<T> {
    body?: AxiosResponse<T>['data']
    headers?: AxiosResponse['headers']
    status?: AxiosResponse['status']
    pagination?: PaginationParams
}

export interface RequestError {
    code: string
    message: string
    status: AxiosResponse['status']
}

export const isAccessTokenRequestError = (err: AxiosError<{ code?: string }>) => {
    return (
        err.response?.status === 401 &&
        (err.response?.data.code === 'AUTH_ERROR' || err.response?.data.code === 'ACCESS_TOKEN_ERROR')
    )
}

export async function requestService<T>(
    url: string,
    options?: AxiosRequestConfig
): Promise<RequestResponse<T> | undefined> {
    try {
        options = options || {
            method: 'GET',
            headers: {},
            params: null,
            data: null
        }

        const response = await axios(url, options)
        if (inRange(response.status, 199, 300)) {
            return {
                body: response.data,
                headers: response.headers,
                status: response.status,
                pagination: {
                    startCursor: response.headers['x-start-cursor'],
                    endCursor: response.headers['x-end-cursor'],
                    totalCount: Number(response.headers['x-total-count']),
                    hasNextPage: response.headers['x-has-next-page'] === 'true',
                    hasPrevPage: response.headers['x-has-prev-page'] === 'true'
                }
            }
        }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (isAccessTokenRequestError(error)) {
                location.href = '/auth/logout'
                return
            }

            if (error.name === 'CanceledError' || error instanceof CanceledError) {
                throw new CanceledError('Request was cancelled')
            }

            throw {
                code: error.response?.data.code,
                message: error.response?.data.message,
                status: error.response?.data.status
            }
        }

        if (error instanceof Error) {
            throw new Error(`Error during request: ${error.message}`)
        }
    }
}
